import React, { useState, useEffect } from 'react';
import dragDropService from '../services/dragDropService';
import './DragDropGame.css';

const DragDropGame = () => {
    const [game, setGame] = useState(null);
    const [levelData, setLevelData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState({
        topic: '',
        difficulty: 'easy'
    });

    // Fixed settings - 30 words total, 3 words per level = 10 levels
    const FIXED_TARGET_SCORE = 30;
    const FIXED_PAIRS_PER_LEVEL = 3;
    const [userGames, setUserGames] = useState([]);
    const [showGamesList, setShowGamesList] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [matchedPairs, setMatchedPairs] = useState(new Set());
    const [wrongMatch, setWrongMatch] = useState(null);
    const [shuffledImages, setShuffledImages] = useState([]);
    const [originalWords, setOriginalWords] = useState([]);

    const topicSuggestions = [
        'động vật', 'trái cây', 'đồ ăn', 'xe cộ', 'đồ vật gia đình',
        'nghề nghiệp', 'thể thao', 'nhạc cụ', 'màu sắc', 'thời tiết'
    ];

    // Shuffle function to randomize array order
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const levelDisplayNames = {
        'easy': 'Dễ',
        'medium': 'Trung bình',
        'hard': 'Khó'
    };

    const handleCreateGame = async () => {
        // Validation
        if (!options.topic || options.topic.trim() === '') {
            setError('Vui lòng nhập chủ đề');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const newGame = await dragDropService.createGame(
                options.topic.trim(),
                options.difficulty,
                FIXED_TARGET_SCORE,
                FIXED_PAIRS_PER_LEVEL
            );

            setGame(newGame);
            await loadCurrentLevel(newGame.gameId);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadCurrentLevel = async (gameId) => {
        setLoading(true);
        try {
            const data = await dragDropService.getCurrentLevel(gameId);
            setLevelData(data);

            // Set matched pairs from backend data
            const completedPairsSet = new Set(data.completedPairs || []);
            setMatchedPairs(completedPairsSet);

            // Shuffle ONLY the images, keep words in original order
            const shuffledImagePairs = shuffleArray(data.pairs || []);
            setShuffledImages(shuffledImagePairs);
            setOriginalWords(data.pairs || []); // Words stay in original order


        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadUserGames = async () => {
        setLoading(true);
        try {
            const result = await dragDropService.getUserGames('in_progress', 20);
            setUserGames(result.games);
            setShowGamesList(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectGame = async (selectedGame) => {
        // Ensure gameId is set correctly
        const gameWithId = {
            ...selectedGame,
            gameId: selectedGame.id || selectedGame.gameId
        };


        setGame(gameWithId);
        setShowGamesList(false);
        await loadCurrentLevel(gameWithId.gameId);
    };

    const handleDragStart = (e, pair) => {
        setDraggedItem(pair);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = (e) => {
        setDraggedItem(null);
        // Reset any styling changes
        setTimeout(() => {
            e.target.style.opacity = '';
            e.target.style.transform = '';
        }, 100);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, targetWord) => {
        e.preventDefault();
        
        if (!draggedItem) return;

        // Check if this is the correct match
        if (draggedItem.word.toLowerCase() === targetWord.toLowerCase()) {
            try {


                if (!game?.gameId) {
                    throw new Error('Game ID không tồn tại');
                }

                const result = await dragDropService.submitMatch(game.gameId, draggedItem.id);
                
                if (result.success) {
                    // Add to matched pairs
                    setMatchedPairs(prev => new Set([...prev, draggedItem.id]));
                    
                    // Update game state
                    setGame(prev => ({
                        ...prev,
                        currentScore: result.newScore,
                        currentLevel: result.currentLevel
                    }));

                    if (result.levelComplete) {
                        if (result.gameComplete) {
                            // Game completed!
                            setGame(prev => ({ ...prev, status: 'completed' }));
                        } else {
                            // Load next level after a short delay
                            setTimeout(() => {
                                loadCurrentLevel(game.gameId);
                            }, 2000);
                        }
                    }
                }
            } catch (err) {
                setError(err.message);
            }
        } else {
            // Wrong match - show error briefly with shake effect
            setWrongMatch(targetWord);
            setError('❌ Sai rồi! Thử lại nhé.');

            // Clear wrong match effect and error after animation
            setTimeout(() => {
                setWrongMatch(null);
                setError('');
            }, 1500);
        }

        setDraggedItem(null);
    };

    const handleMultipleChoiceAnswer = async (pairId, isCorrect) => {
        if (isCorrect) {
            try {
                const result = await dragDropService.submitMatch(game.gameId, pairId);

                if (result.success) {
                    setMatchedPairs(prev => new Set([...prev, pairId]));

                    setGame(prev => ({
                        ...prev,
                        currentScore: result.newScore,
                        currentLevel: result.currentLevel
                    }));

                    if (result.levelComplete) {
                        if (result.gameComplete) {
                            setGame(prev => ({ ...prev, status: 'completed' }));
                        } else {
                            setTimeout(() => {
                                loadCurrentLevel(game.gameId);
                            }, 2000);
                        }
                    }
                }
            } catch (err) {
                setError(err.message);
            }
        } else {
            setError('Sai rồi! Hãy chọn đáp án khác.');
            setTimeout(() => setError(''), 2000);
        }
    };

    const renderGameSetup = () => (
        <div className="game-setup">
            <div className="form-group">
                <label htmlFor="topic-input">Chủ đề</label>
                <input 
                    id="topic-input"
                    type="text" 
                    value={options.topic} 
                    onChange={e => setOptions({...options, topic: e.target.value})}
                    placeholder="VD: động vật, trái cây, đồ ăn..."
                    className="topic-input"
                />
                <div className="topic-suggestions">
                    <span className="suggestions-label">Gợi ý:</span>
                    {topicSuggestions.slice(0, 5).map(suggestion => (
                        <button 
                            key={suggestion}
                            type="button"
                            className="suggestion-btn"
                            onClick={() => setOptions({...options, topic: suggestion})}
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            </div>
            
            <div className="form-row">
                <label htmlFor="difficulty-select">Độ khó</label>
                <select
                    id="difficulty-select"
                    value={options.difficulty}
                    onChange={e => setOptions({...options, difficulty: e.target.value})}
                >
                    <option value="easy">Dễ</option>
                    <option value="medium">Trung bình</option>
                    <option value="hard">Khó</option>
                </select>
            </div>


            
            <div className="button-group">
                <button className="play-btn" onClick={handleCreateGame} disabled={loading}>
                    {loading ? 'Đang tạo game...' : 'Bắt đầu chơi'}
                </button>

                <button className="secondary-btn" onClick={loadUserGames} disabled={loading}>
                    {loading ? 'Đang tải...' : 'Xem games đã tạo'}
                </button>
            </div>
        </div>
    );

    const renderGameContent = () => {
        if (!levelData) return null;



        // Check if level is complete - only if there are pairs and all are matched
        const allPairsMatched = levelData.pairs.length > 0 && levelData.pairs.every(pair => matchedPairs.has(pair.id));

        if (allPairsMatched && game.status === 'completed') {
            return (
                <div className="game-complete">
                    <h3>🎉 Chúc mừng! Bạn đã hoàn thành game! 🎉</h3>
                    <p>Điểm số cuối cùng: <strong>{game.currentScore}/30</strong></p>
                    <div className="action-buttons">
                        <button onClick={() => {setGame(null); setLevelData(null); setShowGamesList(false);}} className="new-game-btn">
                            Chơi game mới
                        </button>
                    </div>
                </div>
            );
        }

        if (allPairsMatched && game.status === 'in_progress') {
            return (
                <div className="level-complete">
                    <h3>✅ Hoàn thành màn {levelData.currentLevel}!</h3>
                    <p>Đang chuyển sang màn tiếp theo...</p>
                </div>
            );
        }

        // Get unmatched pairs for display
        const unmatchedImages = shuffledImages.filter(pair => !matchedPairs.has(pair.id));
        const unmatchedWords = originalWords.filter(pair => !matchedPairs.has(pair.id));



        return (
            <div className="dragdrop-game-area">
                <div className="dragdrop-game-header">
                    <div className="level-info">
                        <div className="level-text">Màn chơi</div>
                        <div className="level-number">{levelData.currentLevel}/{levelData.totalLevels}</div>
                    </div>
                    <div className="score-info">
                        <div className="score-text">Điểm số</div>
                        <div className="score-number">{levelData.currentScore}/{levelData.targetScore}</div>
                    </div>
                </div>

                <div className="dragdrop-game-content">
                    <div className="dragdrop-images-section">
                        <h3 className="section-title">Kéo hình ảnh</h3>
                        <div className="images-grid">
                            {unmatchedImages.map(pair => (
                                <div
                                    key={pair.id}
                                    className={`image-item ${draggedItem?.id === pair.id ? 'dragging' : ''}`}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, pair)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <img src={pair.imageUrl} alt={pair.imageAlt} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="dragdrop-words-section">
                        <h3 className="section-title">Thả vào từ đúng</h3>
                        <div className="words-grid">
                            {originalWords.map(pair => (
                                <div
                                    key={pair.id}
                                    className={`word-slot ${
                                        matchedPairs.has(pair.id) ? 'matched' : ''
                                    } ${
                                        wrongMatch === pair.word ? 'wrong-match' : ''
                                    }`}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, pair.word)}
                                >
                                    {matchedPairs.has(pair.id) ? (
                                        <>
                                            <img src={pair.imageUrl} alt={pair.imageAlt} className="matched-image" />
                                            <div className="matched-word">{pair.word}</div>
                                        </>
                                    ) : (
                                        pair.word
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="action-buttons">
                    <button onClick={() => {setGame(null); setLevelData(null); setShowGamesList(false);}} className="back-btn">
                        Tạo game mới
                    </button>
                </div>
            </div>
        );
    };

    const renderUserGamesList = () => (
        <div className="user-games-list">
            <h3>Games đang chơi của bạn ({userGames.length})</h3>
            {userGames.length > 0 ? (
                <div className="games-grid">
                    {userGames.map((gameItem) => (
                        <div key={gameItem.id} className="game-card" onClick={() => handleSelectGame(gameItem)}>
                            <div className="game-info">
                                <div className="topic-level">{gameItem.topic} - {levelDisplayNames[gameItem.difficulty]}</div>
                                <div className="score-progress">{gameItem.currentScore}/100 điểm</div>
                                <div className="level-progress">Màn {gameItem.currentLevel}/{gameItem.totalLevels}</div>
                            </div>
                            <div className="game-status">
                                <span className="date">{new Date(gameItem.createdAt).toLocaleDateString('vi-VN')}</span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p>Bạn chưa có game nào đang chơi.</p>
            )}
            <button className="back-btn" onClick={() => setShowGamesList(false)}>
                Tạo game mới
            </button>
        </div>
    );

    return (
        <div className="drag-drop-container">
            <div className="drag-drop-content">
                <div style={{width: '100%', maxWidth: '1400px'}}>
                    {error && <p className="error-message">{error}</p>}
            
                    {!game && !showGamesList ? (
                        renderGameSetup()
                    ) : !game && showGamesList ? (
                        renderUserGamesList()
                    ) : game && !levelData ? (
                        <div>Đang tải màn chơi...</div>
                    ) : (
                        renderGameContent()
                    )}
                </div>
            </div>
        </div>
    );
};

export default DragDropGame;
